import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { SUPPORTED_DATE_FORMATS } from '@/constants/common';

import {
  CUSTOM_FIELD_MAX_SINGLE_LINE_LENGTH,
  FILTER_TITLE_MAX_LENGTH,
  INITIATIVE_TITLE_MAX_LENGTH,
} from '../constants';
import { CHARACTER_LIMITS } from '../constants/character-limits';
import { VALIDATION_ERROR_KEY, ValidationResponse } from '../types/validation';
import { transformPercentageToInteger } from '../utils/transform-percentage-to-integer';

dayjs.extend(customParseFormat);

const budgetRegex =
  /(^([$€£]|د\.إ|¥|kr|HK\$|₹|ر\.ق|ر\.س|S\$|R|₨|NZ\$|USD|AUD|EUR|CAD|GBP|AED|JPY|CNY|DKK|HKD|INR|NOK|QAR|SAR|SGD|SEK|ZAR|PKR|NZD)?\s*([\d,.]+)\s*([MK]?)$)|(^([\d,.]+)\s*([MK]?)\s*([$€£]|د\.إ|¥|kr|HK\$|₹|ر\.ق|ر\.س|S\$|R|₨|NZ\$|USD|AUD|EUR|CAD|GBP|AED|JPY|CNY|DKK|HKD|INR|NOK|QAR|SAR|SGD|SEK|ZAR|PKR|NZD)?)$/i;

const validateIfEmpty = (value: string): ValidationResponse => {
  return {
    isValid: !value,
  };
};

export const validateSmallField = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = value.length <= CUSTOM_FIELD_MAX_SINGLE_LINE_LENGTH;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateBigField = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = validateIfEmpty(value).isValid || value.length <= CHARACTER_LIMITS.LARGE_FIELD;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateTitle = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = validateIfEmpty(value).isValid || value.length <= INITIATIVE_TITLE_MAX_LENGTH;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateDescription = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = validateIfEmpty(value).isValid || value.length <= CHARACTER_LIMITS.LARGE_FIELD;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateString = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid =
    validateIfEmpty(value).isValid || value.length <= CUSTOM_FIELD_MAX_SINGLE_LINE_LENGTH;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateFilter = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = validateIfEmpty(value).isValid || value.length <= FILTER_TITLE_MAX_LENGTH;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateMultiFilter = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const isValid = value.split(',').every(item => validateFilter(item).isValid);
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

export const validateChildren = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  // Children are comma-separated initiative titles
  const isValid = value.split(',').every(item => validateTitle(item.trim()).isValid);
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.textTooLong,
    };
  }
  return { isValid };
};

// Keep these unchanged
export const validateDate = (value: string): ValidationResponse => {
  const isValid =
    validateIfEmpty(value).isValid ||
    SUPPORTED_DATE_FORMATS.some(format => dayjs(value, format, true).isValid());
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.wrongDateFormat,
    };
  }
  return { isValid };
};

export const validateNumber = (value: string): ValidationResponse => {
  const isValid = validateIfEmpty(value).isValid || !Number.isNaN(Number(value));
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.invalidNumber,
    };
  }
  return { isValid };
};

export const validateProgressField = (value: string): ValidationResponse => {
  const integer = value ? transformPercentageToInteger(value) : 0;
  const isValidInteger = Number.isInteger(integer);
  const isNumberInRange = integer >= 0 && integer <= 100;
  const isValid = isValidInteger && isNumberInRange;

  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.invalidProgressValue,
    };
  }
  return { isValid };
};

export const validateBudgetField = (value: string, isCSVImport = false): ValidationResponse => {
  if (isCSVImport) {
    return { isValid: true };
  }
  const match = value.trim().match(budgetRegex);
  const isValid = Boolean(match) || validateIfEmpty(value).isValid;
  if (!isValid) {
    return {
      isValid,
      value,
      errorKey: VALIDATION_ERROR_KEY.wrongBudgetFormat,
    };
  }
  return { isValid };
};
